import React, { useEffect, useId, useRef, useState } from 'react';
import {
  EDITORIAL_CORRECTION_PRESENTATIONS,
  resolveQuestionStatusPresentation,
} from '@find-my-part/types';
import { usePublicQuestionSearch, useStyleInjection } from '../../hooks';
import { formatTimeAgo } from '../../lib/formatTimeAgo';
import { canonicalQuestionHref } from '../../lib/questionUrl';

export interface PublicQuestionSearchProps {
  enabled: boolean;
  onAskQuestion: () => void;
  disabledMessage?: string;
  onRetryAvailability?: () => void;
}

export function PublicQuestionSearch({
  enabled,
  onAskQuestion,
  disabledMessage = 'Search opens once the first questions are published.',
  onRetryAvailability,
}: PublicQuestionSearchProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dismissed, setDismissed] = useState(false);
  const inputId = useId();
  const listboxId = `${inputId}-results`;
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const search = usePublicQuestionSearch(query, enabled);

  useStyleInjection(publicQuestionSearchStyles, 'fmp-public-question-search-styles');

  useEffect(() => {
    setActiveIndex(-1);
    setDismissed(false);
    anchorRefs.current = [];
  }, [query, search.questions]);

  const hasResults = search.status === 'results' && search.questions.length > 0;
  const popupOpen = hasResults && !dismissed;
  const manualRetryWaiting =
    search.status === 'rate-limited-manual' && search.retryAtEpochMs !== null;

  useEffect(() => {
    if (!popupOpen || activeIndex < 0) return;
    anchorRefs.current[activeIndex]?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [activeIndex, popupOpen]);

  const retrySearch = (): void => {
    // The action is replaced by the loading state. Move focus deliberately so
    // keyboard users never fall back to the document body on that transition.
    inputRef.current?.focus({ preventScroll: true });
    search.retry();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape' && popupOpen) {
      event.preventDefault();
      setActiveIndex(-1);
      setDismissed(true);
      return;
    }

    if (event.key === 'Tab') {
      setActiveIndex(-1);
      setDismissed(true);
      return;
    }

    if (!hasResults) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setDismissed(false);
      setActiveIndex((current) =>
        current < 0 ? 0 : Math.min(current + 1, search.questions.length - 1)
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setDismissed(false);
      setActiveIndex((current) =>
        current < 0 ? search.questions.length - 1 : Math.max(current - 1, 0)
      );
      return;
    }

    if (event.key === 'Home' && popupOpen) {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (event.key === 'End' && popupOpen) {
      event.preventDefault();
      setActiveIndex(search.questions.length - 1);
      return;
    }

    if (event.key === 'Enter' && popupOpen && activeIndex >= 0) {
      event.preventDefault();
      anchorRefs.current[activeIndex]?.click();
    }
  };

  return (
    <section
      className={`fmp-search ${enabled ? '' : 'fmp-search--disabled'}`}
      onBlur={(event) => {
        const nextTarget = event.relatedTarget;
        if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
          setActiveIndex(-1);
          setDismissed(true);
        }
      }}
    >
      <label className="fmp-search__label" htmlFor={inputId}>
        Describe the part, model, markings or problem
      </label>
      <input
        ref={inputRef}
        id={inputId}
        className="fmp-search__input"
        type="search"
        role="combobox"
        autoComplete="off"
        value={query}
        disabled={!enabled}
        aria-autocomplete="list"
        aria-expanded={popupOpen}
        aria-controls={popupOpen ? listboxId : undefined}
        aria-activedescendant={
          popupOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={enabled ? 'Try “patio roller 81”' : 'Search is not available yet'}
      />

      {!enabled && !onRetryAvailability && (
        <p className="fmp-search__message" role="status">
          {disabledMessage}
        </p>
      )}

      {!enabled && onRetryAvailability && (
        <div className="fmp-search__message fmp-search__message--action" role="status">
          <span>{disabledMessage}</span>
          <button
            type="button"
            className="fmp-btn fmp-btn--secondary"
            onClick={onRetryAvailability}
          >
            Check solved cases again
          </button>
        </div>
      )}

      {enabled && query.trim().length === 1 && (
        <p className="fmp-search__message" role="status">
          Enter at least 2 characters to search.
        </p>
      )}

      {enabled && search.status === 'loading' && (
        <p className="fmp-search__message" role="status" aria-live="polite">
          Searching questions…
        </p>
      )}

      {enabled && search.status === 'empty' && (
        <div className="fmp-search__message fmp-search__message--action" role="status">
          <span>No matches — ask your own question.</span>
          <button type="button" className="fmp-btn fmp-btn--secondary" onClick={onAskQuestion}>
            Ask with photos
          </button>
        </div>
      )}

      {enabled && search.status === 'error' && (
        <div className="fmp-search__message fmp-search__message--action" role="alert">
          <span>Search is temporarily unavailable.</span>
          <button type="button" className="fmp-btn fmp-btn--secondary" onClick={retrySearch}>
            Try search again
          </button>
        </div>
      )}

      {enabled && search.status === 'rate-limited' && (
        <p className="fmp-search__message" role="status">
          Search is paused briefly. Your latest search will resume automatically.
        </p>
      )}

      {enabled && search.status === 'rate-limited-manual' && (
        <div className="fmp-search__message fmp-search__message--action" role="status">
          <span>
            {manualRetryWaiting
              ? 'Search is still paused. Manual retry will be available shortly.'
              : 'Search can be tried again.'}
          </span>
          <button
            type="button"
            className="fmp-btn fmp-btn--secondary"
            disabled={manualRetryWaiting}
            onClick={retrySearch}
          >
            Try search again
          </button>
        </div>
      )}

      {popupOpen && (
        <ul
          id={listboxId}
          className="fmp-search__results"
          role="listbox"
          aria-label="Question search results"
        >
          {search.questions.map((question, index) => {
            const presentation = resolveQuestionStatusPresentation(question.status);
            const optionId = `${listboxId}-option-${index}`;
            return (
              <li key={question.questionId} role="presentation">
                <a
                  id={optionId}
                  ref={(element) => {
                    anchorRefs.current[index] = element;
                  }}
                  role="option"
                  aria-selected={activeIndex === index}
                  tabIndex={-1}
                  href={canonicalQuestionHref(question.slug)}
                  className="fmp-search__option"
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="fmp-search__option-title">{question.title}</span>
                  {question.description && (
                    <span className="fmp-search__option-description">
                      {question.description}
                    </span>
                  )}
                  <span className="fmp-search__option-meta">
                    <span
                      className={`fmp-search__status fmp-search__status--${presentation.tone}`}
                    >
                      {presentation.customerLabel}
                    </span>
                    {question.editoriallyCorrected && (
                      <span>{EDITORIAL_CORRECTION_PRESENTATIONS.CUSTOMER_OR_GUEST}</span>
                    )}
                    <span>
                      {question.replyCount} {question.replyCount === 1 ? 'reply' : 'replies'}
                    </span>
                    <time dateTime={new Date(question.createdAt).toISOString()}>
                      {formatTimeAgo(question.createdAt)}
                    </time>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const publicQuestionSearchStyles = `
  :where(.fmp-widget-scope) .fmp-search {
    position: relative;
    display: grid;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-search__label {
    color: var(--fmp-color-text);
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    line-height: var(--fmp-line-height-heading);
  }

  /* Beat generic host input[type='search'] normalization without !important. */
  :where(.fmp-widget-scope) .fmp-search .fmp-search__input {
    box-sizing: border-box;
  }

  :where(.fmp-widget-scope) .fmp-search__input {
    width: 100%;
    min-height: var(--fmp-size-44);
    padding: var(--fmp-space-md) var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
    background: var(--fmp-color-surface);
    color: var(--fmp-color-text);
    font: inherit;
  }

  :where(.fmp-widget-scope) .fmp-search__input:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-search--disabled {
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-search__message {
    margin: 0;
    color: var(--fmp-color-text-muted);
    font-size: var(--fmp-font-size-sm);
    line-height: var(--fmp-line-height-body);
  }

  :where(.fmp-widget-scope) .fmp-search__message--action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--fmp-space-md);
    padding: var(--fmp-space-md);
    border-radius: var(--fmp-radius-control);
    background: var(--fmp-color-surface-muted);
  }

  :where(.fmp-widget-scope) .fmp-search__results {
    z-index: var(--fmp-z-raised);
    max-height: var(--fmp-size-300);
    margin: 0;
    padding: var(--fmp-space-xs);
    overflow-y: auto;
    list-style: none;
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-surface);
    background: var(--fmp-color-surface);
    box-shadow: var(--fmp-shadow-raised);
  }

  :where(.fmp-widget-scope) .fmp-search__option {
    display: grid;
    gap: var(--fmp-space-xs);
    padding: var(--fmp-space-md);
    border-radius: var(--fmp-radius-control);
    color: var(--fmp-color-text);
    text-decoration: none;
  }

  :where(.fmp-widget-scope) .fmp-search__option[aria-selected='true'],
  :where(.fmp-widget-scope) .fmp-search__option:hover {
    background: var(--fmp-color-primary-light);
  }

  :where(.fmp-widget-scope) .fmp-search__option-title {
    min-width: 0;
    overflow-wrap: anywhere;
    font-weight: var(--fmp-font-weight-semibold);
  }

  :where(.fmp-widget-scope) .fmp-search__option-description {
    overflow: hidden;
    color: var(--fmp-color-text-muted);
    font-size: var(--fmp-font-size-sm);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :where(.fmp-widget-scope) .fmp-search__option-meta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--fmp-space-sm);
    align-items: center;
    color: var(--fmp-color-text-muted);
    font-size: var(--fmp-font-size-xs);
  }

  :where(.fmp-widget-scope) .fmp-search__status {
    padding: var(--fmp-space-xs) var(--fmp-size-10);
    border-radius: var(--fmp-radius-pill);
    background: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-secondary);
    font-weight: var(--fmp-font-weight-semibold);
  }

  :where(.fmp-widget-scope) .fmp-search__status--success {
    background: var(--fmp-color-success-bg);
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-search__status--info {
    background: var(--fmp-color-info-bg);
    color: var(--fmp-color-info);
  }

  :where(.fmp-widget-scope) .fmp-search__status--warning {
    background: var(--fmp-color-warning-bg);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-search__status--danger {
    background: var(--fmp-color-danger-bg);
    color: var(--fmp-color-danger);
  }

  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-search__option[aria-selected='true'] {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) currentColor;
      outline-offset: var(--fmp-outline-inset-offset);
    }
  }

  @media (forced-colors: active) {
    :where(.fmp-widget-scope) .fmp-search__option[aria-selected='true'] {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
      outline-offset: var(--fmp-outline-inset-offset);
    }
  }
`;
